import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Bot, ExternalLink, Pencil, Send, UserCog, X } from "lucide-react";
import apiClient from "@/lib/api-client";
import ConversationMessageContent from "./ConversationMessageContent";
import ConversationDaySeparator from "./ConversationDaySeparator";
import {
  messageFullDate,
  messageTime,
  startsNewDay,
  socialChannelLabel,
  socialChannelStyle,
  type ConversationChannel,
} from "@/lib/conversations";
import { useAuth } from "@/contexts/AuthContext";
import { useConversationPresence } from "@/hooks/useConversationPresence";

/**
 * MINI-INBOX emergente de Conversaciones (14-sep-2026, petición de Luis):
 * se abre sobre la bola verde, como el chat de ayuda — revisas o contestas
 * un WhatsApp y sigues con lo que estabas haciendo, sin navegar. Para la
 * gestión completa (filtros, IA, plantillas de inicio, cierre...) está el
 * enlace a la página completa en la cabecera.
 *
 * Bebe de los MISMOS endpoints que Conversations.tsx (lista, detalle,
 * respuesta) — cero lógica nueva de negocio en el backend.
 */

type ConversationMode = "bot" | "human" | "closed";

interface PopupListItem {
  id: string;
  waId: string;
  mode: ConversationMode;
  unreadCount: number;
  lastMessageAt: string | null;
  locationName: string | null;
  assignedUserId: string | null;
  customer: { firstName: string; lastName: string | null } | null;
  lastMessage: { preview: string; senderType: string; createdAt: string } | null;
  /** Solo en el inbox de PLATAFORMA (superadmin): identificación de EMPRESA
   *  por teléfono en vez de ficha de cliente — diferencia real del contrato,
   *  ver `matchOrganizationByPhone`/`companyDisplayName` en `PlatformSupport.tsx`. */
  channel?: ConversationChannel;
  /** Instagram y Messenger: nombre del perfil de quien escribe (no hay teléfono). */
  contactName?: string | null;
  matchedOrganizationName?: string | null;
  matchedContactName?: string | null;
  matchAmbiguous?: boolean;
  /** Nombre puesto a mano desde el inbox; manda sobre el cruce por teléfono. */
  manualLabel?: string | null;
}

interface PopupMessage {
  id: string;
  direction: "in" | "out";
  senderType: string;
  body: string;
  messageType: string;
  /** Media id de Meta si el mensaje trae adjunto: habilita audio, foto o vídeo. */
  mediaId?: string | null;
  createdAt: string;
}

interface PopupDetail {
  conversation: {
    id: string;
    waId: string;
    mode: ConversationMode;
    customer: { firstName: string; lastName: string | null } | null;
    channel?: ConversationChannel;
    contactName?: string | null;
    matchedOrganizationName?: string | null;
    matchedContactName?: string | null;
    matchAmbiguous?: boolean;
    manualLabel?: string | null;
  };
  messages: PopupMessage[];
  canReplyFreeform: boolean;
}

/** Identidad a mostrar: ficha de cliente del negocio (inquilino) o empresa
 *  emparejada por teléfono (plataforma) — mismo criterio que
 *  `companyDisplayName` de `PlatformSupport.tsx`, sin compartir código porque
 *  el resto del shape difiere demasiado para un helper genérico. */
function contactName(
  item: {
    customer: { firstName: string; lastName: string | null } | null;
    waId: string;
    channel?: ConversationChannel;
    contactName?: string | null;
    matchedOrganizationName?: string | null;
    matchedContactName?: string | null;
    matchAmbiguous?: boolean;
    manualLabel?: string | null;
  },
  isPlatform: boolean,
): string {
  if (isPlatform) {
    // Lo que haya escrito una persona manda sobre cualquier deducción.
    if (item.manualLabel) return item.manualLabel;
    if (item.matchAmbiguous) return "Número ambiguo (varias empresas)";
    if (item.matchedOrganizationName) return item.matchedOrganizationName;
    if (item.channel === "web") return item.matchedContactName ?? "Chat web";
    // Instagram y Facebook de ReservaManager: `waId` no es un teléfono.
    const platformSocial = socialChannelLabel(item.channel);
    if (platformSocial) return item.contactName?.trim() || `Persona de ${platformSocial}`;
    return `+${item.waId}`;
  }
  if (item.customer) {
    return `${item.customer.firstName} ${item.customer.lastName ?? ""}`.trim();
  }
  // Instagram y Messenger: `waId` no es un teléfono.
  const social = socialChannelLabel(item.channel);
  if (social) return item.contactName?.trim() || `Cliente de ${social}`;
  return `+${item.waId}`;
}

function handlerLabel(
  item: { mode: ConversationMode; assignedUserId: string | null },
  currentUserId: string | undefined,
): string {
  if (item.mode === "closed") return "Cerrada";
  if (item.mode === "human") {
    return item.assignedUserId && item.assignedUserId === currentUserId
      ? "La llevas tú"
      : "La lleva el equipo";
  }
  return "La lleva Carmen";
}

function shortTime(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const today = new Date();
  const sameDay =
    d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate();
  return sameDay
    ? d.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleDateString("es-ES", { day: "numeric", month: "short" });
}

export default function ConversationsPopup({
  onClose,
  isPlatform,
}: {
  onClose: () => void;
  /** Superadmin (18-sep-2026): habla con `/admin/platform-conversations` (SU
   *  inbox de soporte) en vez de `/whatsapp-conversations` (el del negocio
   *  seleccionado). Mismo contrato de mode/mensajes/handoff en los dos, ver
   *  `lib/conversations.ts`; difiere la identificación de la fila (empresa
   *  vs. ficha de cliente) y que la plataforma NO tiene `answer-with-ai`
   *  (solo el negocio tiene IA propia respondiendo a sus clientes). */
  isPlatform: boolean;
}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Ruta base ÚNICA para las 7 llamadas del popup y destino del enlace "abrir
  // página completa" — evita mantener el mismo `if` de negocio/plataforma
  // repetido en cada sitio.
  const basePath = isPlatform ? "/admin/platform-conversations" : "/whatsapp-conversations";
  const fullInboxPath = isPlatform ? "/admin/soporte" : "/conversations";
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Chat abierto y a la vista → sin avisos de ese chat (ver el hook).
  useConversationPresence(isPlatform ? null : selectedId);
  const [list, setList] = useState<PopupListItem[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [detail, setDetail] = useState<PopupDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [reopenError, setReopenError] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const sheetRef = useRef<HTMLDivElement>(null);
  // Huella del último detalle pintado: el polling solo re-renderiza si algo
  // cambió de verdad (sin esto, cada tick redibujaba el hilo y se notaba
  // como lag/parpadeo en móvil).
  const detailSignatureRef = useRef("");

  // Con el popup abierto, el scroll de la página de detrás se CONGELA (el
  // contenedor que scrollea es el <main> del Layout, no el body): sin esto,
  // arrastrar dentro del popup movía la lista de atrás.
  //
  // OJO al descongelar (20-sep-2026): se guardaba el valor ANTERIOR y se
  // restauraba tal cual, así que si dos cosas congelaban a la vez, la segunda
  // memorizaba "hidden" y al cerrarse dejaba la página muerta para siempre.
  // Ahora se limpia el estilo en línea y manda otra vez la clase de Tailwind
  // (`overflow-auto`), que es el estado bueno pase lo que pase.
  useEffect(() => {
    const main = document.querySelector("main");
    if (!main) return;
    main.style.overflow = "hidden";
    return () => {
      main.style.overflow = "";
    };
  }, []);

  // TECLADO iOS (14-sep-2026): el teclado encoge el viewport VISUAL pero los
  // elementos fixed se anclan al de maquetación → quedaba una banda blanca
  // muerta entre el composer y el teclado. Mientras el teclado esté abierto,
  // la hoja se redimensiona al área realmente visible (VisualViewport API);
  // al cerrarse, vuelve a mandar el CSS de siempre.
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => {
      const el = sheetRef.current;
      if (!el) return;
      const isMobile = window.matchMedia("(max-width: 639px)").matches;
      const layoutHeight = document.documentElement.clientHeight;
      const hiddenBelow = layoutHeight - vv.height - vv.offsetTop;
      const keyboardOpen = hiddenBelow > 80;
      if (isMobile && keyboardOpen) {
        // iOS desplaza TODA la página para enseñar el campo enfocado. Como la
        // hoja es `position: fixed`, ese desplazamiento se la lleva hacia
        // arriba y lo primero que se pierde es la cabecera: el número y los
        // botones (24-sep-2026). Devolvemos la página a su sitio antes de
        // medir; así `fixed` vuelve a coincidir con lo que se ve.
        if (window.scrollY !== 0) window.scrollTo(0, 0);
        el.style.top = `${Math.max(vv.offsetTop, 0) + 8}px`;
        el.style.height = `${vv.height - 16}px`;
        el.style.bottom = "auto";
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
      } else {
        el.style.top = "";
        el.style.height = "";
        el.style.bottom = "";
      }
    };
    // iOS asienta la geometría TARDE: el evento llega mientras el teclado
    // todavía se está yendo, así que la última medida buena no es la del
    // evento. Sin esta segunda pasada, la hoja se quedaba con la altura de
    // "teclado abierto" (media pantalla) después de cerrarlo — que es como se
    // veía el popup al llegar desde una notificación (20-sep-2026).
    let asentar: ReturnType<typeof setTimeout> | undefined;
    const updateYAsentar = () => {
      update();
      if (asentar) clearTimeout(asentar);
      asentar = setTimeout(update, 250);
    };

    vv.addEventListener("resize", updateYAsentar);
    vv.addEventListener("scroll", updateYAsentar);
    // Al salir del cuadro de texto ya no hay teclado que esquivar: la hoja
    // vuelve a su sitio aunque el navegador no mande ningún evento.
    const sheet = sheetRef.current;
    sheet?.addEventListener("focusout", updateYAsentar);
    return () => {
      if (asentar) clearTimeout(asentar);
      vv.removeEventListener("resize", updateYAsentar);
      vv.removeEventListener("scroll", updateYAsentar);
      sheet?.removeEventListener("focusout", updateYAsentar);
    };
  }, []);

  // ── Lista (poll 15s mientras se está en la vista de lista) ──
  const loadList = useCallback(() => {
    apiClient
      .get<{ success: true; data: PopupListItem[] }>(basePath, {
        params: { page: 1, limit: 20 },
      })
      .then((res) => setList(res.data.data))
      .catch(() => {
        // Silencioso: el popup nunca rompe; la página completa da el error fino.
      })
      .finally(() => setListLoading(false));
  }, [basePath]);

  useEffect(() => {
    if (selectedId) return;
    loadList();
    const timer = setInterval(loadList, 15_000);
    return () => clearInterval(timer);
  }, [selectedId, loadList]);

  // ── Detalle (poll 6s mientras está abierto) ──
  const loadDetail = useCallback((id: string) => {
    apiClient
      .get<{
        success: true;
        data: {
          conversation: PopupDetail["conversation"];
          messages: PopupMessage[];
          canReplyFreeform: boolean;
        };
      }>(`${basePath}/${id}`)
      .then((res) => {
        const d = res.data.data;
        const last = d.messages[d.messages.length - 1];
        // El nombre manual ENTRA en la huella: sin él, guardar un nombre nuevo
        // no repintaba la cabecera y parecía que no se había guardado, aunque
        // en el servidor sí estaba (24-sep-2026).
        const signature = `${id}:${d.messages.length}:${last?.id ?? ""}:${d.conversation.mode}:${d.canReplyFreeform}:${d.conversation.manualLabel ?? ""}`;
        if (signature === detailSignatureRef.current) return; // nada nuevo
        detailSignatureRef.current = signature;
        setDetail({
          conversation: d.conversation,
          messages: d.messages,
          canReplyFreeform: d.canReplyFreeform,
        });
      })
      .catch(() => {
        // Silencioso (ver arriba).
      })
      .finally(() => setDetailLoading(false));
  }, [basePath]);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      return;
    }
    detailSignatureRef.current = "";
    setDetailLoading(true);
    loadDetail(selectedId);
    // 3s: los mensajes entrantes aparecen casi al momento; el guard de la
    // huella evita que el tick redibuje cuando no hay novedades.
    const timer = setInterval(() => loadDetail(selectedId), 3_000);
    return () => clearInterval(timer);
  }, [selectedId, loadDetail]);

  // Autoscroll al fondo del hilo.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [detail?.messages.length, detailLoading]);

  // BORRADOR A SALVO (20-sep-2026). El popup se cierra solo al navegar (p. ej.
  // al tocar una notificación de la campana), y una respuesta a medio escribir
  // no puede evaporarse por eso: se guarda por conversación y vuelve sola al
  // reabrirla. `sessionStorage` puede fallar (modo privado, cookies
  // bloqueadas), así que todo va envuelto: si no se puede guardar, no pasa nada
  // más que perder el borrador, nunca romper el popup.
  const claveBorrador = selectedId ? `rm_borrador_conv_${selectedId}` : null;

  useEffect(() => {
    if (!claveBorrador) return;
    try {
      setText(sessionStorage.getItem(claveBorrador) ?? "");
    } catch {
      setText("");
    }
  }, [claveBorrador]);

  useEffect(() => {
    if (!claveBorrador) return;
    try {
      if (text.trim()) sessionStorage.setItem(claveBorrador, text);
      else sessionStorage.removeItem(claveBorrador);
    } catch {
      // Sin almacenamiento disponible: el borrador simplemente no sobrevive.
    }
  }, [claveBorrador, text]);

  const handleSend = useCallback(async () => {
    if (!selectedId || !detail) return;
    const body = text.trim();
    if (!body || sending) return;
    setSending(true);
    setSendError(false);
    setText("");
    try {
      await apiClient.post(`${basePath}/${selectedId}/messages`, { body });
      loadDetail(selectedId);
    } catch {
      setSendError(true);
      setText(body);
    } finally {
      setSending(false);
    }
  }, [selectedId, detail, text, sending, loadDetail, basePath]);

  // Poner nombre a mano a una conversación del inbox de plataforma: quien
  // escribe desde un número que no está en ninguna ficha solo se ve como un
  // número (caso real: el segundo móvil de la dueña de Centro Elle).
  const [editandoNombre, setEditandoNombre] = useState(false);
  const [nombreBorrador, setNombreBorrador] = useState("");
  const [guardandoNombre, setGuardandoNombre] = useState(false);
  const [errorNombre, setErrorNombre] = useState(false);

  const ponerNombre = useCallback(() => {
    setNombreBorrador(detail?.conversation.manualLabel ?? "");
    setEditandoNombre(true);
  }, [detail]);

  const guardarNombre = useCallback(async () => {
    if (!selectedId) return;
    setGuardandoNombre(true);
    setErrorNombre(false);
    try {
      const label = nombreBorrador.trim();
      await apiClient.patch(`${basePath}/${selectedId}/label`, { label });
      // Se pinta al momento con lo guardado (no se espera al poll, que además
      // puede no repintar si nada más ha cambiado).
      const manualLabel = label || null;
      setDetail((prev) =>
        prev && prev.conversation.id === selectedId
          ? { ...prev, conversation: { ...prev.conversation, manualLabel } }
          : prev,
      );
      setList((prev) =>
        prev.map((c) => (c.id === selectedId ? { ...c, manualLabel } : c)),
      );
      setEditandoNombre(false);
    } catch {
      // Antes se tragaba el error y el nombre "no se guardaba" sin explicación.
      setErrorNombre(true);
    } finally {
      setGuardandoNombre(false);
    }
  }, [selectedId, nombreBorrador, basePath]);

  const handleTakeOver = useCallback(async () => {
    if (!selectedId || actionLoading) return;
    setActionLoading(true);
    try {
      await apiClient.post(`${basePath}/${selectedId}/take-over`);
    } catch {
      // El detalle recargado enseña el estado real.
    } finally {
      loadDetail(selectedId);
      setActionLoading(false);
    }
  }, [selectedId, actionLoading, loadDetail, basePath]);

  // Solo negocio: el inbox de PLATAFORMA no tiene `/answer-with-ai` (no hay
  // una IA propia respondiendo a los hosteleros que escriben a soporte) —
  // diferencia real del contrato, no un olvido. Nunca se llama con
  // `isPlatform === true` (ver el botón condicional más abajo).
  const handleAnswerWithAi = useCallback(async () => {
    if (!selectedId || actionLoading) return;
    setActionLoading(true);
    try {
      await apiClient.post(`${basePath}/${selectedId}/answer-with-ai`);
    } catch {
      // 502 honesto posible (Meta rechazó): el hilo recargado lo refleja.
    } finally {
      // La IA tarda un par de segundos en contestar: recarga con margen
      // (mismo criterio que el botón de la página completa).
      loadDetail(selectedId);
      setTimeout(() => loadDetail(selectedId), 1500);
      setActionLoading(false);
    }
  }, [selectedId, actionLoading, loadDetail, basePath]);

  // Solo plataforma: sustituye a "Que conteste la IA" en modo "human" (ver
  // arriba) — devuelve la conversación al bot sin contestar nada ahora,
  // mismo endpoint que usa el botón "Devolver al bot" de `PlatformSupport.tsx`.
  const handleReturnToBot = useCallback(async () => {
    if (!selectedId || actionLoading) return;
    setActionLoading(true);
    try {
      await apiClient.post(`${basePath}/${selectedId}/return-to-bot`);
    } catch {
      // El detalle recargado enseña el estado real.
    } finally {
      loadDetail(selectedId);
      setActionLoading(false);
    }
  }, [selectedId, actionLoading, loadDetail, basePath]);

  // Plantilla de inicio (reabrir la ventana de 24h) sin salir del popup:
  // mismo endpoint que el boton de la pagina completa.
  const handleReopenWindow = useCallback(async () => {
    if (!selectedId || reopening) return;
    setReopening(true);
    setReopenError(false);
    try {
      await apiClient.post(`${basePath}/start`, {
        conversationId: selectedId,
      });
    } catch {
      setReopenError(true);
    } finally {
      loadDetail(selectedId);
      setReopening(false);
    }
  }, [selectedId, reopening, loadDetail, basePath]);

  const title = detail ? contactName(detail.conversation, isPlatform) : "Conversaciones";
  // Instagram y Facebook llevan su color y su nombre: en la lista van mezcladas
  // con las de WhatsApp y tienen que distinguirse de un vistazo.
  const openChannel = selectedId ? socialChannelStyle(detail?.conversation.channel) : null;
  const headerColor = openChannel?.header ?? "bg-emerald-600";
  const headerHover = openChannel?.headerHover ?? "hover:bg-emerald-500";

  return (
    /* Móvil: hoja FIJA de debajo de la cabecera hasta encima de la bola (una
       altura anclada al botón se salía por arriba y la cabecera quedaba bajo
       el navbar); escritorio (sm+): panel anclado a la bola como el de ayuda. */
    <div ref={sheetRef} className="fixed left-3 right-3 top-[calc(72px+var(--safe-top))] bottom-2 sm:absolute sm:inset-auto sm:bottom-16 sm:right-0 sm:w-[26rem] sm:h-[min(34rem,70vh)] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden">
      {/* Cabecera */}
      <div className={`flex-shrink-0 ${headerColor} text-white px-3 py-2.5 flex items-center gap-2`}>
        {selectedId && (
          <button
            onClick={() => setSelectedId(null)}
            className={`p-1.5 rounded-lg ${headerHover} transition-colors flex-shrink-0`}
            aria-label="Volver a la lista"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
        )}
        <p className="text-sm font-semibold truncate flex-1">
          {title}
          {openChannel && (
            <span className="ml-2 text-[11px] font-medium bg-white/20 rounded px-1.5 py-0.5 align-middle">
              {openChannel.label}
            </span>
          )}
        </p>
        {selectedId && isPlatform && (
          <button
            onClick={() => void ponerNombre()}
            title="Poner un nombre a esta conversación"
            className={`p-1.5 rounded-lg ${headerHover} transition-colors flex-shrink-0`}
          >
            <Pencil className="w-4 h-4" />
          </button>
        )}
        <button
          onClick={() => {
            onClose();
            navigate(selectedId ? `${fullInboxPath}?open=${selectedId}` : fullInboxPath);
          }}
          title="Abrir la página completa"
          className={`p-1.5 rounded-lg ${headerHover} transition-colors flex-shrink-0`}
        >
          <ExternalLink className="w-4 h-4" />
        </button>
        <button
          onClick={onClose}
          className={`p-1.5 rounded-lg ${headerHover} transition-colors flex-shrink-0`}
          aria-label="Cerrar"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {!selectedId ? (
        /* ── LISTA ── */
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain divide-y divide-gray-100">
          {listLoading ? (
            <p className="text-center text-sm text-gray-400 py-8">Cargando...</p>
          ) : list.length === 0 ? (
            <p className="text-center text-sm text-gray-400 py-8 px-4">
              Sin conversaciones todavía.
            </p>
          ) : (
            list.map((item) => {
              const channel = socialChannelStyle(item.channel);
              return (
                <button
                  key={item.id}
                  onClick={() => setSelectedId(item.id)}
                  className={`w-full text-left px-3 py-2.5 hover:bg-gray-50 transition-colors flex items-center gap-2.5 ${
                    channel?.stripe ?? ""
                  }`}
                >
                  <span
                    className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      item.mode === "human"
                        ? "bg-amber-100 text-amber-600"
                        : "bg-emerald-100 text-emerald-600"
                    }`}
                    title={item.mode === "human" ? "La lleva el equipo" : "La lleva Carmen"}
                  >
                    {item.mode === "human" ? (
                      <UserCog className="w-4 h-4" />
                    ) : (
                      <Bot className="w-4 h-4" />
                    )}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center justify-between gap-2">
                      <span className="flex items-center gap-1.5 min-w-0">
                        <span className="text-sm font-medium text-gray-900 truncate">
                          {contactName(item, isPlatform)}
                        </span>
                        {channel && (
                          <span
                            className={`flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-semibold ${channel.chip}`}
                          >
                            {channel.label}
                          </span>
                        )}
                      </span>
                      <span className="text-[11px] text-gray-400 flex-shrink-0">
                        {shortTime(item.lastMessageAt)}
                      </span>
                    </span>
                    <span
                      className={`block text-[11px] truncate ${
                        item.mode === "human" ? "text-amber-600" : "text-emerald-600"
                      }`}
                    >
                      {handlerLabel(item, user?.id)}
                      {item.locationName ? ` · ${item.locationName}` : ""}
                    </span>
                    <span className="flex items-center gap-2">
                      <span className="text-xs text-gray-500 truncate flex-1">
                        {item.lastMessage?.preview ?? "Sin mensajes"}
                      </span>
                      {item.unreadCount > 0 && (
                        <span
                          className={`min-w-[18px] h-[18px] px-1 ${
                            channel?.counter ?? "bg-emerald-500"
                          } text-white text-[11px] font-bold rounded-full flex items-center justify-center flex-shrink-0`}
                        >
                          {item.unreadCount > 9 ? "9+" : item.unreadCount}
                        </span>
                      )}
                    </span>
                  </span>
                </button>
              );
            })
          )}
        </div>
      ) : (
        /* ── DETALLE ── */
        <>
          <div
            ref={scrollRef}
            className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-3 py-3 space-y-2 bg-gray-50"
          >
            {detailLoading && !detail ? (
              <p className="text-center text-sm text-gray-400 py-8">Cargando...</p>
            ) : (
              detail?.messages.map((message, index, messages) => {
                const own = message.direction === "out";
                return (
                  <Fragment key={message.id}>
                    {startsNewDay(message.createdAt, messages[index - 1]?.createdAt) && (
                      <ConversationDaySeparator createdAt={message.createdAt} />
                    )}
                  <div className={`flex ${own ? "justify-end" : "justify-start"}`}>
                    <div
                      className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap break-words ${
                        own
                          ? "bg-emerald-600 text-white rounded-br-md"
                          : "bg-white border border-gray-200 text-gray-800 rounded-bl-md"
                      }`}
                    >
                      {own && (
                        <p
                          className={`text-[11px] font-semibold mb-0.5 ${own ? "text-emerald-100" : "text-gray-400"}`}
                        >
                          {message.senderType === "bot" ? "Carmen" : "Equipo"}
                        </p>
                      )}
                      <ConversationMessageContent
                        message={message}
                        mediaUrl={
                          selectedId ? `${basePath}/${selectedId}/media/${message.id}` : undefined
                        }
                      />
                      <span
                        title={messageFullDate(message.createdAt)}
                        className={`block text-right text-[11px] leading-none mt-1 ${
                          own ? "text-emerald-100" : "text-gray-400"
                        }`}
                      >
                        {messageTime(message.createdAt)}
                      </span>
                    </div>
                  </div>
                  </Fragment>
                );
              })
            )}
          </div>
          {detail && detail.conversation.mode !== "closed" && (
            <div className="flex-shrink-0 border-t border-gray-100 bg-white px-2 pt-2 flex items-center justify-between gap-2">
              <span
                className={`inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-full ${
                  detail.conversation.mode === "human"
                    ? "bg-amber-50 text-amber-700"
                    : "bg-emerald-50 text-emerald-700"
                }`}
              >
                {detail.conversation.mode === "human" ? (
                  <>
                    <UserCog className="w-3.5 h-3.5" /> La lleva el equipo
                  </>
                ) : (
                  <>
                    <Bot className="w-3.5 h-3.5" /> La lleva Carmen
                  </>
                )}
              </span>
              {detail.conversation.mode === "human" ? (
                isPlatform ? (
                  // Sin `answer-with-ai` en plataforma (ver comentario en
                  // `handleReturnToBot`): la única salida de "la lleva el
                  // equipo" es devolverla al bot, no que la IA conteste.
                  <button
                    onClick={() => void handleReturnToBot()}
                    disabled={actionLoading}
                    className="text-xs font-medium px-2.5 py-1 rounded-lg border border-blue-300 text-blue-700 hover:bg-blue-50 disabled:opacity-50 transition-colors"
                  >
                    {actionLoading ? "..." : "Devolver al bot"}
                  </button>
                ) : (
                  <button
                    onClick={() => void handleAnswerWithAi()}
                    disabled={actionLoading}
                    className="text-xs font-medium px-2.5 py-1 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 transition-colors"
                  >
                    {actionLoading ? "..." : "✨ Que conteste la IA"}
                  </button>
                )
              ) : (
                <button
                  onClick={() => void handleTakeOver()}
                  disabled={actionLoading}
                  className="text-xs font-medium px-2.5 py-1 rounded-lg border border-amber-300 text-amber-700 hover:bg-amber-50 disabled:opacity-50 transition-colors"
                >
                  {actionLoading ? "..." : "Tomar el control"}
                </button>
              )}
            </div>
          )}
          <div className="flex-shrink-0 bg-white p-2">
            {detail && !detail.canReplyFreeform && detail.conversation.mode !== "closed" ? (
              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                {openChannel ? (
                  // En Instagram y Facebook no hay mensaje de inicio que enviar.
                  <p>
                    Han pasado más de 24h desde su último mensaje: en {openChannel.label} solo
                    se puede contestar dentro de ese plazo. Hay que esperar a que vuelva a
                    escribir.
                  </p>
                ) : (
                  <>
                    <p>
                      Ventana de 24h cerrada: WhatsApp solo permite texto libre 24h
                      después del último mensaje del cliente.
                    </p>
                    {reopenError && (
                      <p className="text-red-600 mt-1">
                        No se pudo enviar. Inténtalo de nuevo.
                      </p>
                    )}
                    <button
                      onClick={() => void handleReopenWindow()}
                      disabled={reopening}
                      className="mt-2 w-full px-3 py-1.5 rounded-lg bg-emerald-600 text-white font-medium hover:bg-emerald-700 disabled:opacity-50 transition-colors"
                    >
                      {reopening ? "Enviando..." : "Enviar mensaje de inicio"}
                    </button>
                  </>
                )}
              </div>
            ) : detail?.conversation.mode === "closed" ? (
              <p className="text-xs text-gray-500 px-3 py-2">
                Conversación cerrada — se reabre sola si el cliente escribe.
              </p>
            ) : (
              <>
                {sendError && (
                  <p className="text-xs text-red-600 px-1 pb-1">
                    No se pudo enviar. Inténtalo de nuevo.
                  </p>
                )}
                <div className="flex items-end gap-2">
                  <textarea
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onFocus={() => {
                      // Con el teclado abriéndose, mantener el final del hilo
                      // a la vista (iOS desplaza el viewport al enfocar).
                      setTimeout(() => {
                        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
                      }, 300);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        void handleSend();
                      }
                    }}
                    rows={1}
                    maxLength={4096}
                    placeholder="Responder..."
                    className="flex-1 resize-none px-3 py-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent max-h-24"
                  />
                  <button
                    onClick={() => void handleSend()}
                    disabled={!text.trim() || sending}
                    className="p-2.5 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 transition-colors flex-shrink-0"
                    aria-label="Enviar"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </>
            )}
          </div>
        </>
      )}

      {/* Ventana propia (no una fila dentro de la hoja): en el móvil, al
          enfocar un campo dentro de la hoja flotante se dispara el reajuste
          por teclado y el campo se perdía de vista (24-sep-2026). Centrada y
          por encima de todo, se comporta como el resto de modales. */}
      {editandoNombre && selectedId && (
        <div
          // En el móvil el teclado tapa la mitad de abajo: la ventana se
          // pega arriba para que el campo quede siempre a la vista.
          className="fixed inset-0 z-[80] flex items-start justify-center bg-black/40 p-4 pt-16 sm:items-center sm:pt-4"
          onClick={() => setEditandoNombre(false)}
        >
          <div
            className="w-full max-w-sm rounded-xl bg-white p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-base font-semibold text-gray-900">
              Nombre de esta conversación
            </h3>
            <p className="mt-1 text-xs text-gray-500">
              Se usa en tu bandeja para reconocer quién escribe. Déjalo vacío
              para volver al nombre automático por teléfono.
            </p>
            <input
              autoFocus
              value={nombreBorrador}
              onChange={(e) => setNombreBorrador(e.target.value.slice(0, 80))}
              onKeyDown={(e) => {
                if (e.key === "Enter") void guardarNombre();
                if (e.key === "Escape") setEditandoNombre(false);
              }}
              placeholder="María (Centro Elle)"
              className="mt-3 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
            {errorNombre && (
              <p className="mt-2 text-xs text-red-600">
                No se ha podido guardar. Inténtalo otra vez.
              </p>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setEditandoNombre(false)}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={() => void guardarNombre()}
                disabled={guardandoNombre}
                className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                {guardandoNombre ? "Guardando…" : "Guardar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
